import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Folder, 
  File, 
  FileText, 
  Image, 
  FileSpreadsheet, 
  FileCode,
  Download, 
  Trash2, 
  Upload,
  Package,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Map,
  Building2,
  Users,
  Truck,
  Shield,
  Camera,
  Calendar,
  AlertCircle,
  HardDrive,
  Eye,
  FolderPlus,
  X,
  Plus
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { 
  getProjectFiles, 
  uploadProjectFile, 
  deleteProjectFile,
  downloadProjectZip,
  createProjectSubfolder
} from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for folder types
const FOLDER_ICONS = {
  'map': Map,
  'building': Building2,
  'users': Users,
  'truck': Truck,
  'shield': Shield,
  'camera': Camera,
  'calendar': Calendar,
  'file-text': FileText,
  'folder': Folder,
};

// Icon mapping for file types
const FILE_TYPE_ICONS = {
  'pdf': FileText,
  'image': Image,
  'word': FileText,
  'excel': FileSpreadsheet,
  'autocad': FileCode,
  'archicad': FileCode,
  'ifc': FileCode,
  'sketchup': FileCode,
  'design': Image,
  'archive': Package,
  'other': File,
};

// File type colors
const FILE_TYPE_COLORS = {
  'pdf': 'text-red-500',
  'image': 'text-green-500',
  'word': 'text-blue-500',
  'excel': 'text-emerald-500',
  'autocad': 'text-orange-500',
  'archicad': 'text-purple-500',
  'ifc': 'text-cyan-500',
  'sketchup': 'text-amber-500',
  'design': 'text-pink-500',
  'archive': 'text-gray-500',
  'other': 'text-slate-400',
};

// Preview Modal Component with navigation
const PreviewModal = ({ file, folderId, projetId, onClose, previewableFiles, onNavigate }) => {
  const token = localStorage.getItem('token');
  const baseUrl = `${API}/api/projets/${projetId}/files/${folderId}/${encodeURIComponent(file.name)}?token=${token}`;
  const previewUrl = `${baseUrl}&preview=true`;
  const downloadUrl = baseUrl;
  
  const isPdf = file.type === 'pdf';
  const isImage = file.type === 'image';

  const currentIndex = previewableFiles ? previewableFiles.findIndex(f => f.name === file.name) : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < (previewableFiles ? previewableFiles.length - 1 : 0);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && hasPrev) {
        onNavigate(previewableFiles[currentIndex - 1]);
      } else if (e.key === 'ArrowRight' && hasNext) {
        onNavigate(previewableFiles[currentIndex + 1]);
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, hasPrev, hasNext, previewableFiles, onNavigate, onClose]);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="preview-modal" onClick={onClose}>
      <div className="relative max-w-5xl max-h-[90vh] w-full mx-4" onClick={e => e.stopPropagation()}>
        {/* Close button */}
        <button
          onClick={onClose}
          data-testid="preview-close-btn"
          className="absolute -top-10 right-0 text-white hover:text-gray-300 p-2"
        >
          <X className="w-6 h-6" />
        </button>
        
        {/* File name + counter */}
        <div className="absolute -top-10 left-0 text-white font-medium truncate max-w-[70%] flex items-center gap-3">
          <span>{file.name}</span>
          {previewableFiles && previewableFiles.length > 1 && (
            <span className="text-xs text-white/60 bg-white/10 px-2 py-0.5 rounded" data-testid="preview-counter">
              {currentIndex + 1} / {previewableFiles.length}
            </span>
          )}
        </div>

        {/* Previous arrow */}
        {hasPrev && (
          <button
            onClick={(e) => { e.stopPropagation(); onNavigate(previewableFiles[currentIndex - 1]); }}
            data-testid="preview-prev-btn"
            className="absolute -left-14 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Next arrow */}
        {hasNext && (
          <button
            onClick={(e) => { e.stopPropagation(); onNavigate(previewableFiles[currentIndex + 1]); }}
            data-testid="preview-next-btn"
            className="absolute -right-14 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}

        {/* Content */}
        <div className="bg-white rounded-lg overflow-hidden">
          {isPdf && (
            <iframe
              src={previewUrl}
              className="w-full h-[80vh]"
              title={file.name}
              data-testid="preview-pdf-iframe"
            />
          )}
          {isImage && (
            <img
              src={previewUrl}
              alt={file.name}
              className="max-w-full max-h-[80vh] mx-auto object-contain"
              data-testid="preview-image"
            />
          )}
        </div>

        {/* Download button */}
        <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2">
          <a
            href={downloadUrl}
            download={file.name}
            data-testid="preview-download-btn"
            className="inline-flex items-center px-4 py-2 bg-white text-slate-800 rounded-lg hover:bg-slate-100"
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger
          </a>
        </div>
      </div>
    </div>
  );
};

// Create Subfolder Modal
const CreateSubfolderModal = ({ parentFolder, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Veuillez entrer un nom de dossier');
      return;
    }
    
    setCreating(true);
    try {
      await onCreate(name.trim());
      onClose();
    } catch (err) {
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">
          Créer un sous-dossier dans "{parentFolder.name}"
        </h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Nom du dossier
          </label>
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Ex: Photos 2026-03-27"
            autoFocus
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
          />
          <p className="text-xs text-slate-500 mt-1">
            Conseil: utilisez des dates pour organiser vos photos (ex: 2026-03-27)
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button 
            onClick={handleCreate} 
            disabled={creating || !name.trim()}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {creating ? 'Création...' : 'Créer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ProjectFileManager = ({ projetId, projetName }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [failedUploads, setFailedUploads] = useState({}); // {folderId: [{file, error}]}
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewFolder, setPreviewFolder] = useState(null);
  const [showSubfolderModal, setShowSubfolderModal] = useState(null);
  const fileInputRefs = useRef({});

  // Compress image using canvas
  const compressImage = (file, maxWidth = 1600, quality = 0.6) => {
    return new Promise((resolve) => {
      // Only compress images
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Resize if larger than maxWidth
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            if (blob && blob.size < file.size) {
              // Try File constructor first. On some iOS Safari versions, `new File(...)` throws TypeError.
              // Fall back to the Blob with a `.name` attached so the rest of the code keeps working.
              let compressed;
              try {
                compressed = new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });
              } catch (err) {
                // iOS Safari < 15.4 fallback: attach meta on the blob itself
                compressed = blob;
                try {
                  Object.defineProperty(compressed, 'name', { value: file.name, writable: false });
                  Object.defineProperty(compressed, 'lastModified', { value: Date.now(), writable: false });
                } catch (_) { /* read-only on some browsers — ignore */ }
              }
              const savings = Math.round((1 - blob.size / file.size) * 100);
              console.log(`Compressed ${file.name}: ${(file.size/1024/1024).toFixed(1)}Mo → ${(blob.size/1024/1024).toFixed(1)}Mo (-${savings}%)`);
              resolve(compressed);
            } else {
              resolve(file); // Keep original if compression didn't help
            }
          }, 'image/jpeg', quality);
        };
        img.onerror = () => resolve(file);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getProjectFiles(projetId);
      setData(res.data);
      
      // Auto-expand folders with files
      const expanded = {};
      res.data.folders.forEach(folder => {
        if (folder.file_count > 0 || (folder.subfolders && folder.subfolders.length > 0)) {
          expanded[folder.id] = true;
        }
      });
      setExpandedFolders(expanded);
    } catch (err) {
      toast.error('Erreur lors du chargement des fichiers');
    } finally {
      setLoading(false);
    }
  }, [projetId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const handleFileUpload = async (folderId, files, options = {}) => {
    if (!files || files.length === 0) return;

    const total = files.length;
    // Adaptive compression: smaller images for bulk uploads → less memory on iPhone
    const maxWidth = total > 80 ? 1024 : total > 20 ? 1280 : 1600;
    const quality = total > 80 ? 0.55 : 0.6;
    const concurrency = options.concurrency ?? 3;

    setUploading(prev => ({ ...prev, [folderId]: true }));
    setUploadProgress(prev => ({ ...prev, [folderId]: { current: 0, total, compressing: true, done: 0 } }));
    setFailedUploads(prev => ({ ...prev, [folderId]: [] }));

    // Try to keep iPhone awake during long uploads
    let wakeLock = null;
    try {
      if ('wakeLock' in navigator && total > 10) {
        wakeLock = await navigator.wakeLock.request('screen');
      }
    } catch (_) {}

    let doneCount = 0;
    let successCount = 0;
    const failures = [];
    let totalSaved = 0;

    const processOne = async (file, idx) => {
      let attempt = 0;
      while (attempt < 3) {
        try {
          setUploadProgress(prev => ({
            ...prev,
            [folderId]: { ...(prev[folderId] || {}), current: idx, total, compressing: true, fileName: file.name, done: doneCount }
          }));
          const originalSize = file.size;
          const processedFile = await compressImage(file, maxWidth, quality);
          totalSaved += originalSize - processedFile.size;
          setUploadProgress(prev => ({
            ...prev,
            [folderId]: { ...(prev[folderId] || {}), current: idx, total, compressing: false, fileName: file.name, done: doneCount }
          }));
          await uploadProjectFile(projetId, folderId, processedFile);
          successCount++;
          return true;
        } catch (err) {
          attempt++;
          if (attempt >= 3) {
            failures.push({ file, error: err.message || 'Upload failed' });
            return false;
          }
          // exponential backoff: 1s, 2s
          await new Promise(r => setTimeout(r, 1000 * attempt));
        }
      }
    };

    // Concurrency pool
    const queue = files.map((f, i) => ({ f, i }));
    const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
      while (queue.length > 0) {
        const item = queue.shift();
        if (!item) break;
        await processOne(item.f, item.i);
        doneCount++;
        setUploadProgress(prev => ({
          ...prev,
          [folderId]: { ...(prev[folderId] || {}), done: doneCount, total }
        }));
      }
    });
    await Promise.all(workers);

    if (wakeLock) { try { await wakeLock.release(); } catch (_) {} }

    setUploading(prev => ({ ...prev, [folderId]: false }));
    setUploadProgress(prev => ({ ...prev, [folderId]: null }));
    setFailedUploads(prev => ({ ...prev, [folderId]: failures }));

    if (successCount > 0) {
      const savedMo = (totalSaved / 1024 / 1024).toFixed(1);
      const msg = totalSaved > 100000
        ? `${successCount}/${total} fichier(s) uploadé(s) (${savedMo} Mo économisés)`
        : `${successCount}/${total} fichier(s) uploadé(s)`;
      toast.success(msg);
      fetchFiles();
    }
    if (failures.length > 0) {
      toast.error(`${failures.length} fichier(s) en erreur — utilisez "Réessayer" pour reprendre.`);
    }

    setExpandedFolders(prev => ({ ...prev, [folderId]: true }));
  };

  const retryFailedUploads = async (folderId) => {
    const failed = failedUploads[folderId] || [];
    if (failed.length === 0) return;
    const files = failed.map(f => f.file);
    await handleFileUpload(folderId, files);
  };

  const handleFileInputChange = (folderId, event) => {
    const files = Array.from(event.target.files);
    handleFileUpload(folderId, files);
    event.target.value = '';
  };

  const handleDrop = (folderId, event) => {
    event.preventDefault();
    setDragOverFolder(null);
    
    const files = Array.from(event.dataTransfer.files);
    handleFileUpload(folderId, files);
  };

  const handleDragOver = (folderId, event) => {
    event.preventDefault();
    setDragOverFolder(folderId);
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleDownloadFile = (folderId, filename) => {
    const token = localStorage.getItem('token');
    const url = `${API}/api/projets/${projetId}/files/${folderId}/${encodeURIComponent(filename)}?token=${token}`;
    
    // Create a temporary link and click it
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePreviewFile = (file, folderId) => {
    if (file.type === 'pdf' || file.type === 'image') {
      setPreviewFile(file);
      setPreviewFolder(folderId);
    } else {
      // For non-previewable files, just download
      handleDownloadFile(folderId, file.name);
    }
  };

  // Get previewable files from the current preview folder
  const getPreviewableFiles = (folderId) => {
    if (!data || !folderId) return [];
    const findFolder = (folders) => {
      for (const f of folders) {
        if (f.id === folderId) return f;
        if (f.subfolders) {
          const found = findFolder(f.subfolders);
          if (found) return found;
        }
      }
      return null;
    };
    const folder = findFolder(data.folders);
    if (!folder) return [];
    return folder.files.filter(f => f.type === 'pdf' || f.type === 'image');
  };

  const handleDeleteFile = async (folderId, filename) => {
    if (!window.confirm(`Supprimer "${filename}" ?`)) return;

    try {
      await deleteProjectFile(projetId, folderId, filename);
      toast.success('Fichier supprimé');
      fetchFiles();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadZip = async () => {
    try {
      setDownloadingZip(true);
      const res = await downloadProjectZip(projetId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `MAAG_${projetName.replace(/\s+/g, '_')}_${date}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('ZIP téléchargé !');
    } catch (err) {
      toast.error('Erreur lors du téléchargement du ZIP');
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleCreateSubfolder = async (parentFolderId, subfolderName) => {
    try {
      await createProjectSubfolder(projetId, parentFolderId, subfolderName);
      toast.success(`Dossier "${subfolderName}" créé`);
      fetchFiles();
    } catch (err) {
      throw err;
    }
  };

  const getFileIcon = (fileType) => {
    const Icon = FILE_TYPE_ICONS[fileType] || File;
    return Icon;
  };

  const getFolderIcon = (iconName) => {
    const Icon = FOLDER_ICONS[iconName] || Folder;
    return Icon;
  };

  const canPreview = (fileType) => {
    return fileType === 'pdf' || fileType === 'image';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
        <span className="ml-2 text-slate-600">Chargement des fichiers...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
        <p className="text-slate-600">Erreur lors du chargement</p>
        <Button onClick={fetchFiles} className="mt-4">
          Réessayer
        </Button>
      </div>
    );
  }

  // Render a single folder (can be main or subfolder)
  const renderFolder = (folder, isSubfolder = false, parentId = null) => {
    const FolderIcon = getFolderIcon(folder.icon);
    const folderId = parentId ? `${parentId}/${folder.id}` : folder.id;
    const isExpanded = expandedFolders[folderId];
    const isDragOver = dragOverFolder === folderId;
    const isUploading = uploading[folderId];

    return (
      <div 
        key={folderId} 
        className={`border rounded-lg overflow-hidden transition-all ${
          isDragOver ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'
        } ${isSubfolder ? 'ml-6 mt-2' : ''}`}
        onDrop={(e) => handleDrop(folderId, e)}
        onDragOver={(e) => handleDragOver(folderId, e)}
        onDragLeave={handleDragLeave}
      >
        {/* Folder header */}
        <div 
          className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50"
          onClick={() => toggleFolder(folderId)}
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
            <FolderIcon className={`w-5 h-5 ${isSubfolder ? 'text-blue-400' : 'text-amber-500'}`} />
            <span className="font-medium text-slate-800">{folder.name}</span>
            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
              {folder.file_count} fichier(s)
            </span>
            {folder.size > 0 && (
              <span className="text-xs text-slate-400">
                {folder.size_human}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            {isUploading ? (
              <div className="flex items-center gap-2 text-xs text-indigo-600 font-medium px-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                {uploadProgress[folderId] ? (
                  <span data-testid={`upload-progress-${folderId}`}>
                    {uploadProgress[folderId].done ?? 0}/{uploadProgress[folderId].total}
                  </span>
                ) : 'Upload...'}
              </div>
            ) : (
              <>
                {failedUploads[folderId] && failedUploads[folderId].length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => retryFailedUploads(folderId)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                    title={`${failedUploads[folderId].length} fichier(s) en échec — cliquez pour réessayer`}
                    data-testid={`retry-upload-${folderId}`}
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Réessayer ({failedUploads[folderId].length})
                  </Button>
                )}
                {/* Create subfolder button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSubfolderModal(folder)}
                  className="text-slate-400 hover:text-blue-600"
                  title="Créer un sous-dossier"
                >
                  <FolderPlus className="w-4 h-4" />
                </Button>
                
                <input
                  type="file"
                  multiple
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.dwg"
                  ref={el => fileInputRefs.current[folderId] = el}
                  onChange={(e) => handleFileInputChange(folderId, e)}
                  className="hidden"
                  data-testid={`file-input-${folderId}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRefs.current[folderId]?.click()}
                  className="text-slate-400 hover:text-indigo-600"
                  data-testid={`upload-btn-${folderId}`}
                  title="Ajouter des fichiers"
                >
                  <Upload className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Folder content */}
        {isExpanded && (
          <div className="border-t border-slate-100">
            {/* Subfolders */}
            {folder.subfolders && folder.subfolders.length > 0 && (
              <div className="p-2">
                {folder.subfolders.map(subfolder => renderFolder(subfolder, true, folder.id))}
              </div>
            )}
            
            {/* Files */}
            {folder.files.length === 0 && (!folder.subfolders || folder.subfolders.length === 0) ? (
              <div 
                className={`px-4 py-8 text-center ${isDragOver ? 'bg-indigo-50' : 'bg-slate-50'}`}
              >
                <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragOver ? 'text-indigo-500' : 'text-slate-300'}`} />
                <p className={`text-sm ${isDragOver ? 'text-indigo-600 font-medium' : 'text-slate-400'}`}>
                  {isDragOver ? 'Déposez ici !' : 'Glissez-déposez des fichiers ou cliquez sur ↑'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {folder.files.map(file => {
                  const FileIcon = getFileIcon(file.type);
                  const colorClass = FILE_TYPE_COLORS[file.type] || 'text-slate-400';
                  const previewable = canPreview(file.type);

                  return (
                    <div 
                      key={file.id}
                      className="flex items-center justify-between px-4 py-2 hover:bg-slate-50"
                    >
                      <div 
                        className={`flex items-center gap-3 flex-1 min-w-0 ${previewable ? 'cursor-pointer' : ''}`}
                        onClick={() => previewable && handlePreviewFile(file, folderId)}
                      >
                        <FileIcon className={`w-5 h-5 flex-shrink-0 ${colorClass}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate" title={file.name}>
                            {file.name}
                            {previewable && (
                              <span className="ml-2 text-xs text-indigo-500">(cliquez pour voir)</span>
                            )}
                          </p>
                          <p className="text-xs text-slate-400">
                            {file.size_human} • {new Date(file.modified).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {previewable && (
                          <button
                            onClick={() => handlePreviewFile(file, folderId)}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 rounded-lg hover:bg-indigo-50"
                            title="Aperçu"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDownloadFile(folderId, file.name)}
                          className="p-1.5 text-slate-400 hover:text-green-600 rounded-lg hover:bg-green-50"
                          title="Télécharger"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteFile(folderId, file.name)}
                          className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                
                {/* Drop zone at bottom */}
                <div 
                  className={`px-4 py-3 text-center border-t border-dashed ${
                    isDragOver ? 'bg-indigo-50 border-indigo-300' : 'border-slate-200'
                  }`}
                >
                  <p className={`text-xs ${isDragOver ? 'text-indigo-600' : 'text-slate-400'}`}>
                    {isDragOver ? '📁 Déposez ici' : '+ Glissez pour ajouter des fichiers'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="project-file-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-indigo-600" />
            Serveur Projet
          </h3>
          <p className="text-sm text-slate-500">
            {data.total_files} fichier(s) • {data.total_size_human}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchFiles}
            data-testid="refresh-files-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button
            onClick={handleDownloadZip}
            disabled={downloadingZip}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="download-zip-btn"
          >
            <Package className="w-4 h-4 mr-2" />
            {downloadingZip ? 'Création...' : 'Télécharger ZIP'}
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>💡 Conseils :</strong>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Cliquez sur un PDF ou une image pour l'<strong>aperçu</strong> sans téléchargement</li>
              <li>Utilisez le bouton <FolderPlus className="w-3 h-3 inline" /> pour créer des <strong>sous-dossiers</strong> (ex: Photos par date)</li>
              <li>Cliquez sur "Télécharger ZIP" pour sauvegarder tout le projet</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Folders */}
      <div className="space-y-2">
        {data.folders.map(folder => renderFolder(folder))}
      </div>

      {/* Preview Modal */}
      {previewFile && (
        <PreviewModal
          file={previewFile}
          folderId={previewFolder}
          projetId={projetId}
          previewableFiles={getPreviewableFiles(previewFolder)}
          onNavigate={(newFile) => setPreviewFile(newFile)}
          onClose={() => {
            setPreviewFile(null);
            setPreviewFolder(null);
          }}
        />
      )}

      {/* Create Subfolder Modal */}
      {showSubfolderModal && (
        <CreateSubfolderModal
          parentFolder={showSubfolderModal}
          onClose={() => setShowSubfolderModal(null)}
          onCreate={(name) => handleCreateSubfolder(showSubfolderModal.id, name)}
        />
      )}
    </div>
  );
};

export default ProjectFileManager;
